import React, { useEffect, useState } from "react";

import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { blue, grey } from "@material-ui/core/colors";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import { openAiModels, geminiModels, getModelLabel } from "../../constants/aiModels";

// Modelos padrão pré-selecionados (melhor custo-benefício para mensagens)
const DEFAULT_OPENAI_MODEL = "gpt-4o-mini";
const DEFAULT_GEMINI_MODEL = "gemini-2.5-flash";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  fullWidth: {
    width: "100%",
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
  sectionTitle: {
    fontSize: "16px",
    color: blue[700],
    marginBottom: theme.spacing(1),
  },
  sectionSubtitle: {
    color: grey[600],
    fontSize: "13px",
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
}));

export default function AISettings(props) {
  const { oldSettings } = props;
  const classes = useStyles();
  const { update } = useSettings();

  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [geminiApiKey, setGeminiApiKey] = useState("");
  const [openaiModel, setOpenaiModel] = useState(DEFAULT_OPENAI_MODEL);
  const [geminiModel, setGeminiModel] = useState(DEFAULT_GEMINI_MODEL);

  const [showOpenaiApiKey, setShowOpenaiApiKey] = useState(false);
  const [showGeminiApiKey, setShowGeminiApiKey] = useState(false);

  const [loadingOpenaiApiKey, setLoadingOpenaiApiKey] = useState(false);
  const [loadingGeminiApiKey, setLoadingGeminiApiKey] = useState(false);
  const [loadingOpenaiModel, setLoadingOpenaiModel] = useState(false);
  const [loadingGeminiModel, setLoadingGeminiModel] = useState(false);

  useEffect(() => {
    if (Array.isArray(oldSettings) && oldSettings.length) {
      const findValue = (key) =>
        oldSettings.find((s) => s.key === key)?.value;

      const oKey = findValue("openaiApiKey");
      if (oKey !== undefined) setOpenaiApiKey(oKey || "");

      const gKey = findValue("geminiApiKey");
      if (gKey !== undefined) setGeminiApiKey(gKey || "");

      const oModel = findValue("openaiDefaultModel");
      if (oModel) setOpenaiModel(oModel);

      const gModel = findValue("geminiDefaultModel");
      if (gModel) setGeminiModel(gModel);
    }
  }, [oldSettings]);

  const saveSetting = async (key, value) => {
    await update({ key, value: value || "" });
  };

  const handleSaveOpenaiApiKey = async () => {
    setLoadingOpenaiApiKey(true);
    try {
      await saveSetting("openaiApiKey", openaiApiKey);
    } catch (e) {
      toast.error("Erro ao salvar a API Key do ChatGPT.");
    }
    setLoadingOpenaiApiKey(false);
  };

  const handleSaveGeminiApiKey = async () => {
    setLoadingGeminiApiKey(true);
    try {
      await saveSetting("geminiApiKey", geminiApiKey);
    } catch (e) {
      toast.error("Erro ao salvar a API Key do Gemini.");
    }
    setLoadingGeminiApiKey(false);
  };

  const handleChangeOpenaiModel = async (value) => {
    setOpenaiModel(value);
    setLoadingOpenaiModel(true);
    try {
      await saveSetting("openaiDefaultModel", value);
    } catch (e) {
      toast.error("Erro ao salvar o modelo padrão do ChatGPT.");
    }
    setLoadingOpenaiModel(false);
  };

  const handleChangeGeminiModel = async (value) => {
    setGeminiModel(value);
    setLoadingGeminiModel(true);
    try {
      await saveSetting("geminiDefaultModel", value);
    } catch (e) {
      toast.error("Erro ao salvar o modelo padrão do Gemini.");
    }
    setLoadingGeminiModel(false);
  };

  return (
    <Grid spacing={3} container className={classes.container}>
      <Grid xs={12} item>
        <Typography className={classes.sectionSubtitle}>
          Defina aqui as credenciais e o modelo padrão de cada provedor. Elas são
          usadas automaticamente sempre que uma API Key não for informada nos
          fluxos, prompts ou regras de acionamento.
        </Typography>
      </Grid>

      {/* CHATGPT / OPENAI */}
      <Grid xs={12} item>
        <Typography className={classes.sectionTitle}>ChatGPT (OpenAI)</Typography>
      </Grid>
      <Grid xs={12} sm={7} item>
        <TextField
          className={classes.fullWidth}
          label="API Key ChatGPT"
          type={showOpenaiApiKey ? "text" : "password"}
          variant="outlined"
          value={openaiApiKey}
          onChange={(e) => setOpenaiApiKey(e.target.value)}
          onBlur={handleSaveOpenaiApiKey}
          helperText={loadingOpenaiApiKey ? "Salvando..." : "Modelos GPT / o (o3, o4-mini...)"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowOpenaiApiKey((s) => !s)}
                  edge="end"
                >
                  {showOpenaiApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid xs={12} sm={5} item>
        <FormControl variant="outlined" className={classes.selectContainer}>
          <InputLabel id="openai-model-label">Modelo padrão</InputLabel>
          <Select
            labelId="openai-model-label"
            label="Modelo padrão"
            value={openaiModel}
            onChange={(e) => handleChangeOpenaiModel(e.target.value)}
          >
            {openAiModels.map((model) => (
              <MenuItem key={model} value={model}>
                {getModelLabel(model)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {loadingOpenaiModel ? "Salvando..." : "Recomendado: GPT-4o mini (custo-benefício)"}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid xs={12} item>
        <Divider className={classes.divider} />
      </Grid>

      {/* GEMINI / GOOGLE */}
      <Grid xs={12} item>
        <Typography className={classes.sectionTitle}>Gemini (Google)</Typography>
      </Grid>
      <Grid xs={12} sm={7} item>
        <TextField
          className={classes.fullWidth}
          label="API Key Gemini"
          type={showGeminiApiKey ? "text" : "password"}
          variant="outlined"
          value={geminiApiKey}
          onChange={(e) => setGeminiApiKey(e.target.value)}
          onBlur={handleSaveGeminiApiKey}
          helperText={loadingGeminiApiKey ? "Salvando..." : "Modelos Gemini"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowGeminiApiKey((s) => !s)}
                  edge="end"
                >
                  {showGeminiApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid xs={12} sm={5} item>
        <FormControl variant="outlined" className={classes.selectContainer}>
          <InputLabel id="gemini-model-label">Modelo padrão</InputLabel>
          <Select
            labelId="gemini-model-label"
            label="Modelo padrão"
            value={geminiModel}
            onChange={(e) => handleChangeGeminiModel(e.target.value)}
          >
            {geminiModels.map((model) => (
              <MenuItem key={model} value={model}>
                {getModelLabel(model)}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {loadingGeminiModel ? "Salvando..." : "Recomendado: Gemini 2.5 Flash (custo-benefício)"}
          </FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );
}
